import { groupBy } from "lodash";
import { mapObject } from "../../../../utils/mapObject";
export const flatMapBreakdown = (theClass) => (breakdown) => {
    return breakdown.securities.map((s) => ({
        cusip: s.cusip,
        ticker: s.ticker,
        name: s.name,
        category: breakdown.category,
        class: theClass,
    }));
};
export const mapFlatList = (breakdown) => {
    const b = (breakdown.portfolioAssetClassBreakdown ?? []).map((assetClassCategory) => mapObject(assetClassCategory, (val, assetClassName) => val === undefined ? [] : flatMapBreakdown(assetClassName)(val)));
    return b.flat(2);
};
export const groupTaxLots = (taxLots) => {
    return groupBy(taxLots, (lot) => lot.cusip);
};
export const hydrateSecurities = (groupedTaxLots) => (securities) => {
    const totalMarketValue = mapObject(groupedTaxLots, (val) => val.flatMap((v) => v.marketValue))
        .flat()
        .reduce((prev, curr) => prev + curr, 0);
    return securities.map((s) => {
        const taxLots = groupedTaxLots[s.cusip] ?? [];
        const marketValue = taxLots.reduce((prev, curr) => prev + curr.marketValue, 0);
        const totalSpentBuying = taxLots.reduce((prev, curr) => prev + curr.buyPrice * curr.quantity, 0);
        const quantity = taxLots.reduce((prev, curr) => prev + curr.quantity, 0);
        const classMarketValue = securities
            .filter((aSecurity) => s.category === aSecurity.category)
            .filter((aSecurity) => s.class === aSecurity.class)
            .reduce((securityMVSum, currentSecurity) => securityMVSum +
            (groupedTaxLots[currentSecurity.cusip] ?? []).reduce((taxLotSum, currTaxLot) => taxLotSum + currTaxLot.marketValue, 0), 0);
        const categoryMarketValue = securities
            .filter((aSecurity) => s.category === aSecurity.category)
            .reduce((securityMVSum, currentSecurity) => securityMVSum +
            (groupedTaxLots[currentSecurity.cusip] ?? []).reduce((taxLotSum, currTaxLot) => taxLotSum + currTaxLot.marketValue, 0), 0);
        return {
            ...s,
            marketValue,
            percentOfTotal: marketValue / totalMarketValue,
            quantity,
            costBasis: totalSpentBuying / quantity,
            percentOfClass: marketValue / classMarketValue,
            percentOfCategory: marketValue / categoryMarketValue,
        };
    });
};
/**
 * This creates nested categories and classes for a security list. Categories
 * can have multiple classes.
 *
 * ### Algorithm
 * 1. Create a full security (named hydrated security) that reduces all tax lot
 * information into into a single set of values
 * 2. Group securities by their category (largest organizational folder)
 * 3. For each category's securities, sub-group by class
 * 4. For each class, return an object with aggregated information
 * 5. For each category (and it's included classes) return an object with
 * aggregated information
 *
 * ### Known Gotchas
 * - Class names are not unique between Categories. For example both Categories
 * "Cash" and "Unclassified" have the "Other" classes.
 */
export const createClassAndCategories = (breakdown, taxLots) => {
    const groupedTaxLots = groupTaxLots(taxLots);
    const flatSecurities = mapFlatList(breakdown);
    const hydrated = hydrateSecurities(groupedTaxLots)(flatSecurities);
    const totalMarketValue = hydrated.reduce((prev, curr) => prev + curr.marketValue, 0);
    const groupedByCategory = groupBy(hydrated, (h) => h.category);
    const categories = mapObject(groupedByCategory, (securitiesOfThisCategory, theCategory) => {
        const marketValueOfCategory = securitiesOfThisCategory.reduce((prev, curr) => prev + curr.marketValue, 0);
        const groupedByClass = groupBy(securitiesOfThisCategory, (s) => s.class);
        const classList = mapObject(groupedByClass, (securitiesOfThisClass, theClass) => {
            const marketValueOfClass = securitiesOfThisClass.reduce((prev, curr) => prev + curr.marketValue, 0);
            return {
                class: theClass,
                percentOfTotal: marketValueOfClass / totalMarketValue,
                percentOfCategory: marketValueOfClass / marketValueOfCategory,
                marketValue: marketValueOfClass,
                securities: securitiesOfThisClass,
                category: theCategory,
            };
        });
        return {
            category: theCategory,
            percentOfTotal: marketValueOfCategory / totalMarketValue,
            marketValue: marketValueOfCategory,
            classes: classList,
        };
    });
    return categories;
};
//# sourceMappingURL=utils.js.map